/**
 * @file src/routes/routes.jsx
 * @description 
 * Authoritative Dynamic Route Generator.
 * Orchestrates the transition from Static (Public) to Dynamic (Registry-driven) routes.
 * 
 * @actor System/UI_Orchestrator
 * @action Route/Generation
 * @metric 4.1, 13.4, 18.2
 */
import React, { lazy } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

// The catch-all. Resolves the path against the content-managed pages in the
// dictionary and falls back to NotFound, so a published page is reachable
// without a route line here — see modules/marketing/pages/CmsPage.jsx.
const CmsPage = lazy(() => import('../modules/marketing/pages/CmsPage.jsx'));
const InternalServerError = lazy(() => import('../modules/errors/pages/InternalServerError'));
const About = lazy(() => import('../modules/marketing/pages/About'));
// 🎖️ The public case for reviewing. Deliberately honest about the bar —
// a high, clearly stated one is the most attractive thing you can show
// somebody who can clear it.
const BecomeReviewer = lazy(() => import('../modules/marketing/pages/BecomeReviewer.jsx'));
const ReviewerOnboardingStart = lazy(() => import('../modules/marketing/pages/ReviewerOnboardingStart.jsx'));
// Reviewer plan terms, readable before applying — commission is what
// somebody actually weighs.
const ReviewerPricing = lazy(() => import('../modules/marketing/pages/ReviewerPricing.jsx'));
// Dev-only design-system specimen (/dev/buttons). Never mounted in a production build.
const ButtonSpecimen = lazy(() => import('../modules/dev/pages/ButtonSpecimen.jsx'));
// Hoisted on purpose: Vite's define step skips `import.meta.env` when a spread's
// `...` sits right before it, so the inline form reached the browser unreplaced.
// The path is a constant, not a `path: '…'` literal, so the public-route
// inventories (navLinks.test, publicPaths.test, the dictionary's RESERVED_SLUGS)
// do not count a dev-only page as a public route.
const DEV_SPECIMEN_PATH = '/dev/buttons';
const DEV_ONLY_ROUTES = import.meta.env.DEV
  ? [{ path: DEV_SPECIMEN_PATH, element: <GuestLayout><ButtonSpecimen /></GuestLayout> }]
  : [];
const Pricing = lazy(() => import('../modules/marketing/pages/Pricing'));
const IntegrationsDirectory = lazy(() => import('../modules/marketing/pages/IntegrationsDirectory.jsx'));
const IntegrationPage = lazy(() => import('../modules/marketing/pages/IntegrationPage.jsx'));
const HelpCenter = lazy(() => import('../modules/marketing/pages/HelpCenter'));
const SearchResults = lazy(() => import('../modules/marketing/pages/SearchResults'));

import { useDynamicCatalog } from '../hooks/useDynamicCatalog';
import { componentMap } from '../registry/componentMap';
import { ProtectedRoute } from './ProtectedRoute';
import { AdminRoute } from './AdminRoute';
import Layout from '../core/Layout';
import GuestLayout from '../core/GuestLayout';

const Login = lazy(() => import('../modules/auth/pages/Login'));
const Register = lazy(() => import('../modules/auth/pages/Register'));
// Where a provider returns the browser after a social sign-in.
const ExternalCallback = lazy(() => import('../modules/auth/pages/ExternalCallback'));
const PasswordResetRequest = lazy(() => import('../modules/auth/pages/PasswordResetRequest'));
const PasswordResetConfirm = lazy(() => import('../modules/auth/pages/PasswordResetConfirm'));
const VerifyEmail = lazy(() => import('../modules/auth/pages/VerifyEmail'));
const MainDashboard = lazy(() => import('../modules/identity/pages/Dashboard'));
const UserProfile = lazy(() => import('../modules/identity/pages/Profile'));
const WelcomeOnboarding = lazy(() => import('../modules/identity/pages/WelcomeOnboarding'));
const PurchasesWorkspace = lazy(() => import('../modules/billing/pages/PurchasesWorkspace.jsx'));
const MyLadder = lazy(() => import('../modules/progression/pages/MyLadder.jsx'));
const MyMoxWallet = lazy(() => import('../modules/progression/pages/MyMoxWallet.jsx'));
const EarnGuidePage = lazy(() => import('../modules/progression/pages/EarnGuidePage.jsx'));
const MyConciergeRequests = lazy(() => import('../modules/concierge/pages/MyConciergeRequests.jsx'));
const RequirementsAI = lazy(() => import('../modules/requirements/pages/RequirementsAI'));
const RequirementsV2 = lazy(() => import('../modules/requirements/pages/RequirementsV2.jsx'));
const RequirementsReport = lazy(() => import('../modules/requirements/pages/RequirementsReport.jsx'));
const PublicReport = lazy(() => import('../modules/requirements/pages/PublicReport.jsx'));
const InviteLanding = lazy(() => import('../modules/requirements/pages/InviteLanding.jsx'));
const ClaimProposal = lazy(() => import('../modules/requirements/pages/ClaimProposal.jsx'));
const UnsubscribeProposal = lazy(() => import('../modules/requirements/pages/UnsubscribeProposal.jsx'));
const SourcingPool = lazy(() => import('../modules/requirements/sourcing/SourcingPool.jsx'));
const PoolRunPage = lazy(() => import('../modules/requirements/sourcing/PoolRunPage.jsx'));
const PoolIssuesPage = lazy(() => import('../modules/requirements/sourcing/PoolIssuesPage.jsx'));
const MyReviewsPage = lazy(() => import('../modules/requirements/pages/MyReviewsPage.jsx'));
const ProjectRecord = lazy(() => import('../modules/requirements/pages/ProjectRecord.jsx'));
// Requirements v2 admin console (M-093 / M-009 / M-022 / M-006 / M-005
// admin sides). AdminRoute gates owner-group at the boundary.
const RequirementsV2AdminHome = lazy(() => import('../modules/requirements/admin/AdminHomePage.jsx'));
const RequirementsV2FeatureFlags = lazy(() => import('../modules/requirements/admin/FeatureFlagsPage.jsx'));
const RequirementsV2AuditLog = lazy(() => import('../modules/requirements/admin/AuditLogPage.jsx'));
const RequirementsV2Disputes = lazy(() => import('../modules/requirements/admin/DisputesPage.jsx'));
const TwoPersonApprovals = lazy(() => import('../modules/requirements/admin/TwoPersonApprovalsPage.jsx'));
const RequirementsV2ExpertApplications = lazy(() => import('../modules/requirements/admin/ExpertApplicationsPage.jsx'));
const ExpertApplicationDetail = lazy(() => import('../modules/requirements/admin/ExpertApplicationDetailPage.jsx'));
// Content hierarchy — Pillars -> Sub-pillars -> Requirements -> Base
// Templates -> Document Category Templates. Phase 1: data model + CRUD
// only, not yet wired into real document generation.
const RequirementsV2Pillars = lazy(() => import('../modules/requirements/admin/PillarsPage.jsx'));
const RequirementsV2SubPillars = lazy(() => import('../modules/requirements/admin/SubPillarsPage.jsx'));
const RequirementsV2RequirementsCatalog = lazy(() => import('../modules/requirements/admin/RequirementsCatalogPage.jsx'));
const RequirementsV2BaseTemplate = lazy(() => import('../modules/requirements/admin/BaseTemplatePage.jsx'));
const RequirementsV2MasterTemplate = lazy(() => import('../modules/requirements/admin/MasterTemplatePage.jsx'));
const RequirementsV2DocCategoryTemplates = lazy(() => import('../modules/requirements/admin/DocumentCategoryTemplatesPage.jsx'));
const RequirementsV2TemplateProposals = lazy(() => import('../modules/requirements/admin/TemplateProposalsPage.jsx'));
const RequirementsV2BaseSolutionDocument = lazy(() => import('../modules/requirements/admin/BaseSolutionDocumentPage.jsx'));
const RequirementsV2Experiment = lazy(() => import('../modules/requirements/admin/ExperimentPage.jsx'));
const RequirementsV2ExperimentRuns = lazy(() => import('../modules/requirements/admin/ExperimentRunsPage.jsx'));
const RequirementsV2WorkflowVersion = lazy(() => import('../modules/requirements/admin/WorkflowVersionPage.jsx'));
const RequirementsV2WorkflowTemplates = lazy(() => import('../modules/requirements/admin/WorkflowTemplatesPage.jsx'));
const RequirementsV2Journeys = lazy(() => import('../modules/requirements/admin/JourneysPage.jsx'));
const RequirementsV2WhoWasWrong = lazy(() => import('../modules/requirements/admin/WhoWasWrongPage.jsx'));
const RequirementsV2Examples = lazy(() => import('../modules/requirements/admin/ExamplesPage.jsx'));
const ExampleDetail = lazy(() => import('../modules/home/pages/ExampleDetail.jsx'));
const RequirementsV2Review = lazy(() => import('../modules/requirements/admin/ReviewPage.jsx'));
const RequirementsV2ProposalClusters = lazy(() => import('../modules/requirements/admin/ProposalClustersPage.jsx'));
const RequirementsV2DataManagement = lazy(() => import('../modules/requirements/admin/DataManagementPage.jsx'));
const KnowledgeCorpus = lazy(() => import('../modules/knowledge/pages/KnowledgeCorpusPage.jsx'));
const KnowledgeType = lazy(() => import('../modules/knowledge/pages/KnowledgeTypePage.jsx'));
const OutputReports = lazy(() => import('../modules/knowledge/pages/OutputReportsPage.jsx'));
const Schemas = lazy(() => import('../modules/knowledge/pages/SchemasPage.jsx'));
const DataHome = lazy(() => import('../modules/knowledge/pages/DataPage.jsx'));
const ProjectFields = lazy(() => import('../modules/knowledge/pages/ProjectFieldsPage.jsx'));
const OutputReportCategory = lazy(() => import('../modules/knowledge/pages/OutputReportCategoryPage.jsx'));
const OutputReportDetail = lazy(() => import('../modules/knowledge/pages/OutputReportDetailPage.jsx'));
const Patterns = lazy(() => import('../modules/patterns/pages/PatternsPage.jsx'));
const Functions = lazy(() => import('../modules/functions/pages/FunctionsPage.jsx'));
const Configurations = lazy(() => import('../modules/configurations/pages/ConfigurationsPage.jsx'));
const Templates = lazy(() => import('../modules/templates/pages/TemplatesPage.jsx'));
const TemplateDetail = lazy(() => import('../modules/templates/pages/TemplateDetailPage.jsx'));
const LearningMetrics = lazy(() => import('../modules/learning/pages/LearningMetricsPage.jsx'));
const RetrievalPreview = lazy(() => import('../modules/learning/pages/RetrievalPreviewPage.jsx'));
const JudgingQueue = lazy(() => import('../modules/learning/pages/JudgingQueuePage.jsx'));
const LearningRuns = lazy(() => import('../modules/learning/pages/LearningRunsPage.jsx'));
const RequirementsV2FactorWeights = lazy(() => import('../modules/requirements/admin/FactorWeightsPage.jsx'));
const RequirementsV2ReportFormat = lazy(() => import('../modules/requirements/admin/ReportFormatPage.jsx'));
const RequirementsV2ReportStructure = lazy(() => import('../modules/requirements/admin/ReportStructurePage.jsx'));
const RequirementsV2MasterSolutionDocument = lazy(() => import('../modules/requirements/admin/MasterSolutionDocumentPage.jsx'));
const ReportManager = lazy(() => import('../modules/reports-manager/pages/ReportManagerPage.jsx'));
const RequirementsV2CategorySolutionDocument = lazy(() => import('../modules/requirements/admin/CategorySolutionDocumentPage.jsx'));
// Unified Home — replaces CustomerHome + OperatorConsole + ReviewerHome
// visually. Same component at every persona's landing route; the page
// asks `useRole()` to decide which sections to render. Routes left
// alone so deep-link / login redirects keep working.
const CustomerHome  = lazy(() => import('../modules/home/pages/Home.jsx'));
// Post-Stripe-Checkout landing page. Polls for the webhook-confirmed
// subscription, then bounces the user back to the page they kicked
// off the upgrade from (review modal, plans page, etc).
const BillingSuccess = lazy(() => import('../modules/billing/pages/BillingSuccess.jsx'));
const BillingCancel = lazy(() => import('../modules/billing/pages/BillingCancel.jsx'));
const PlatformSettings = lazy(() => import('../modules/admin-infra/pages/PlatformSettings.jsx'));
// Super-admin only: the secret key is a credential and the protected-action
// list decides whether the platform's front door is guarded.
const CaptchaSettings = lazy(() => import('../modules/admin-infra/pages/CaptchaSettings.jsx'));
const AuthProviders = lazy(() => import('../modules/admin-infra/pages/AuthProviders.jsx'));
const UsersAdminDashboard = lazy(() => import('../modules/identity/pages/UsersAdminDashboard.jsx'));
const UsersAdminSettings = lazy(() => import('../modules/identity/pages/UsersAdminSettings.jsx'));
// /admin/console also renders the unified Home so operators and
// super-admins land on the same surface as everyone else — just with
// different sections lit up.
const OperatorConsole = lazy(() => import('../modules/home/pages/Home.jsx'));
// Payments module — admin and customer sub-pages.
//   PaymentsHome (Overview)   → /billing/payments
//   PaymentsTransactions      → /admin/payments/transactions
//   PaymentsDisputes          → /admin/payments/disputes
//   PaymentsProviders         → /admin/payments/providers
//   PaymentSettings (Stripe)  → /admin/payments/settings
const PaymentsHome         = lazy(() => import('../modules/payments/pages/PaymentsHome.jsx'));
const PaymentsTransactions = lazy(() => import('../modules/payments/pages/PaymentsTransactions.jsx'));
const PaymentsDisputes     = lazy(() => import('../modules/payments/pages/PaymentsDisputes.jsx'));
const PaymentsProviders    = lazy(() => import('../modules/payments/pages/PaymentsProviders.jsx'));
const PaymentSettings      = lazy(() => import('../modules/admin-infra/pages/PaymentSettings.jsx'));
// Social sign-in credentials. Super-admin only — the server enforces it too,
// but AdminRoute keeps the page out of reach of an operator who cannot use it.
const IdentityProviderSettings = lazy(() => import('../modules/admin-infra/pages/IdentityProviderSettings.jsx'));
// Review marketplace policy — kill-switch, caps, section eligibility.
// AdminRoute gates owner-group at the boundary; the page itself
// disables write controls for non-super operators (server enforces).
const ReviewPolicy         = lazy(() => import('../modules/admin-infra/pages/ReviewPolicy.jsx'));
const ExpertReviewQueue = lazy(() => import('../modules/requirements/pages/ExpertReviewQueue.jsx'));
const ReviewerWorkConsole = lazy(() => import('../modules/reviewer/pages/ReviewerWorkConsole.jsx'));
// The requirements-side earnings ledger. Distinct from the payments
// Stripe payout tab on the reviewer dashboard — see the page header.
const ReviewerEarningsPage = lazy(() => import('../modules/reviewer/pages/ReviewerEarningsPage.jsx'));
// 🎖️ The reviewer's own plan. Separate from /billing, which is the same
// person's CUSTOMER subscription — two prices on one screen is how
// somebody cancels the wrong one.
const ReviewerPlanPage = lazy(() => import('../modules/reviewer/pages/ReviewerPlanPage.jsx'));
// Admin side of the same thing — commission, concurrency, payout hold.
const ReviewerPlansAdmin = lazy(() => import('../modules/admin-infra/pages/ReviewerPlansAdmin.jsx'));
// The two dials that decide who becomes a reviewer, and how many.
const CalibrationSeedsAdmin = lazy(() => import('../modules/admin-infra/pages/CalibrationSeedsAdmin.jsx'));
// What the reviewer is accredited for + their unlock meters. Both read
// models existed with no client — staff could see them, the reviewer
// they describe could not.
const MyStandingPage = lazy(() => import('../modules/reviewer/pages/MyStandingPage.jsx'));
const DelegationsPage = lazy(() => import('../modules/reviewer/pages/DelegationsPage.jsx'));
const CalibrationPage = lazy(() => import('../modules/reviewer/pages/CalibrationPage.jsx'));
const ReviewerDirectoryPage = lazy(() => import('../modules/reviewer/pages/ReviewerDirectoryPage.jsx'));
const ReviewerAvailabilityPage = lazy(() => import('../modules/reviewer/pages/ReviewerAvailabilityPage.jsx'));
const ReviewerFeedbackPage = lazy(() => import('../modules/reviewer/pages/ReviewerFeedbackPage.jsx'));
const DelegateSectionsPage = lazy(() => import('../modules/reviewer/pages/DelegateSectionsPage.jsx'));
const ApplicationRailPage = lazy(() => import('../modules/reviewer/pages/ApplicationRailPage.jsx'));
const EvidencePage = lazy(() => import('../modules/reviewer/pages/EvidencePage.jsx'));
const ProposeTemplateChangePage = lazy(() => import('../modules/reviewer/pages/ProposeTemplateChangePage.jsx'));
const StaffQueuesPage = lazy(() => import('../modules/requirements/admin/StaffQueuesPage.jsx'));
const ProjectDetail = lazy(() => import('../modules/projects/pages/ProjectDetail.jsx'));
const DisputePage = lazy(() => import('../modules/requirements/pages/DisputePage.jsx'));
const ReviewPipeline = lazy(() => import('../modules/admin-infra/pages/ReviewPipeline.jsx'));
const ProjectPreview = lazy(() => import('../modules/projects/pages/ProjectPreview.jsx'));
const SolutionReport = lazy(() => import('../modules/reports/pages/SolutionReport.jsx'));
const Pillars = lazy(() => import('../modules/billing/pages/Pillars.jsx'));
const BillingHub = lazy(() => import('../modules/billing/pages/BillingHub.jsx'));
import { RequirePaidTier } from '../components/blocks/tier-gate.block.jsx';
const ConciergeQueue = lazy(() => import('../modules/concierge/pages/ConciergeQueue.jsx'));
const ReviewerDashboard = lazy(() => import('../modules/reviewer/pages/ReviewerDashboard.jsx'));
const PublicProfile = lazy(() => import('../modules/identity/pages/PublicProfile.jsx'));
// 3-screen service modules (config-driven shells).
// Each module is one config + three thin page wrappers; see
// audit.config.js as the reference. Per-entity sub-routes pass an
// `entity` prop into the same shell.
const AuditHome = lazy(() => import('../modules/audit/pages/AuditHome.jsx'));
const AuditList = lazy(() => import('../modules/audit/pages/AuditList.jsx'));
const AuditDetail = lazy(() => import('../modules/audit/pages/AuditDetail.jsx'));
const AssessmentTaxonomyPage = lazy(() => import('../modules/requirements/admin/AssessmentTaxonomyPage.jsx'));
const EventsHome = lazy(() => import('../modules/events/pages/EventsHome.jsx'));
const EventsList = lazy(() => import('../modules/events/pages/EventsList.jsx'));
const EventsDetail = lazy(() => import('../modules/events/pages/EventsDetail.jsx'));
const NotificationsHome = lazy(() => import('../modules/notifications/pages/NotificationsHome.jsx'));
const NotificationsList = lazy(() => import('../modules/notifications/pages/NotificationsList.jsx'));
const NotificationsDetail = lazy(() => import('../modules/notifications/pages/NotificationsDetail.jsx'));
// Per-user notification preferences — sits under /settings/notifications
// and is exposed via the Account section in the sidebar.
const NotificationPreferences = lazy(() => import('../modules/notifications/pages/NotificationPreferences.jsx'));
const JobsHome = lazy(() => import('../modules/jobs/pages/JobsHome.jsx'));
const JobsList = lazy(() => import('../modules/jobs/pages/JobsList.jsx'));
const JobsDetail = lazy(() => import('../modules/jobs/pages/JobsDetail.jsx'));
const OrchestratorHome = lazy(() => import('../modules/orchestrator/pages/OrchestratorHome.jsx'));
const OrchestratorList = lazy(() => import('../modules/orchestrator/pages/OrchestratorList.jsx'));
const OrchestratorDetail = lazy(() => import('../modules/orchestrator/pages/OrchestratorDetail.jsx'));
const RegistryHome = lazy(() => import('../modules/registry/pages/RegistryHome.jsx'));
const RegistryList = lazy(() => import('../modules/registry/pages/RegistryList.jsx'));
const RegistryDetail = lazy(() => import('../modules/registry/pages/RegistryDetail.jsx'));
const CommunicationsHome = lazy(() => import('../modules/communications/pages/CommunicationsHome.jsx'));
const CommunicationsList = lazy(() => import('../modules/communications/pages/CommunicationsList.jsx'));
const CommunicationsDetail = lazy(() => import('../modules/communications/pages/CommunicationsDetail.jsx'));
const CommunicationsSettings = lazy(() => import('../modules/communications/pages/CommunicationsSettings.jsx'));
// GDPR self-service: export (Art. 20) + erasure (Art. 17). The page was built
// but never routed — a data-subject right with no way in.
const MyDataPage = lazy(() => import('../modules/requirements/privacy/MyDataPage.jsx'));
// API keys, webhooks, the contract and limits — the member-facing developer surface.
const DeveloperPage = lazy(() => import('../modules/identity/pages/DeveloperPage.jsx'));
// Customer-facing unified message inbox (customer↔reviewer chat threads).
const MessagesInbox = lazy(() => import('../modules/communications/pages/MessagesInbox.jsx'));
// PaymentsHome / PaymentsList / PaymentsDetail are imported higher up
// in this file (line ~70) as part of the new Payments module wiring.
// The legacy /admin/payments/control/* routes still reference List +
// Detail by entity name, so we keep those bindings active there.
const PaymentsList = lazy(() => import('../modules/payments/pages/PaymentsList.jsx'));
const PaymentsDetail = lazy(() => import('../modules/payments/pages/PaymentsDetail.jsx'));
const SubscriptionsHome = lazy(() => import('../modules/subscriptions/pages/SubscriptionsHome.jsx'));
const SubscriptionsList = lazy(() => import('../modules/subscriptions/pages/SubscriptionsList.jsx'));
const SubscriptionsDetail = lazy(() => import('../modules/subscriptions/pages/SubscriptionsDetail.jsx'));
import downloadsMemberConfig from '../modules/downloads/downloads.member.config.js';
const DownloadsHome = lazy(() => import('../modules/downloads/pages/DownloadsHome.jsx'));
const DownloadsList = lazy(() => import('../modules/downloads/pages/DownloadsList.jsx'));
const DownloadsDetail = lazy(() => import('../modules/downloads/pages/DownloadsDetail.jsx'));
const StorageHome = lazy(() => import('../modules/storage/pages/StorageHome.jsx'));
const StorageList = lazy(() => import('../modules/storage/pages/StorageList.jsx'));
const StorageDetail = lazy(() => import('../modules/storage/pages/StorageDetail.jsx'));
const AiLlmHome = lazy(() => import('../modules/ai-llm/pages/AiLlmHome.jsx'));
const AiLlmActiveProvider = lazy(() => import('../modules/ai-llm/pages/AiLlmActiveProvider.jsx'));
const AiLlmList = lazy(() => import('../modules/ai-llm/pages/AiLlmList.jsx'));
const AiLlmDetail = lazy(() => import('../modules/ai-llm/pages/AiLlmDetail.jsx'));
const AiMemoryHome = lazy(() => import('../modules/ai-memory/pages/AiMemoryHome.jsx'));
const AiMemoryList = lazy(() => import('../modules/ai-memory/pages/AiMemoryList.jsx'));
const AiMemoryDetail = lazy(() => import('../modules/ai-memory/pages/AiMemoryDetail.jsx'));
const AiPolicyHome = lazy(() => import('../modules/ai-policy/pages/AiPolicyHome.jsx'));
const AiPolicyList = lazy(() => import('../modules/ai-policy/pages/AiPolicyList.jsx'));
const AiPolicyDetail = lazy(() => import('../modules/ai-policy/pages/AiPolicyDetail.jsx'));
const OrgManager = lazy(() => import('../modules/identity/pages/OrgManager'));
const DictionaryHome = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryHome.jsx'));
const DictionaryDataTypes = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryDataTypes.jsx'));
const DictionaryCategoryEntries = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryCategoryEntries.jsx'));
const DictionaryEntryEdit = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryEntryEdit.jsx'));
const DictionaryEntryDelete = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryEntryDelete.jsx'));
const DictionaryTemplates = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryTemplates.jsx'));
const DictionaryTemplateEdit = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryTemplateEdit.jsx'));
const DictionaryTemplateDelete = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryTemplateDelete.jsx'));
const DictionaryGlobalSettings = lazy(() => import('../modules/resource-vault/pages/dictionary/DictionaryGlobalSettings.jsx'));

// Operator console (priority #5) — owner-group governance pages.
const EligibilityMatrix = lazy(() => import('../modules/admin-infra/pages/EligibilityMatrix.jsx'));
const ReviewerEarnings  = lazy(() => import('../modules/admin-infra/pages/ReviewerEarnings.jsx'));
const CommonsPage       = lazy(() => import('../modules/commons/pages/CommonsPage.jsx'));
const CommonsWindowPage = lazy(() => import('../modules/commons/pages/CommonsWindowPage.jsx'));
const CommonsMePage     = lazy(() => import('../modules/commons/pages/CommonsMePage.jsx'));
const CommonsConsole    = lazy(() => import('../modules/commons/pages/CommonsConsole.jsx'));
const CommonsGovernancePage = lazy(() => import('../modules/commons/pages/CommonsGovernancePage.jsx'));
// Accreditation promotion board. Distinct from ReviewerEarnings, which
// reads the payments ladder — this one is the L0–L4 accreditation one.
const ReviewerPromotions = lazy(() => import('../modules/admin-infra/pages/ReviewerPromotions.jsx'));
const EnquiriesQueue = lazy(() => import('../modules/admin-infra/pages/EnquiriesQueue.jsx'));
// The RV0–RV6 candidate queue and stage decisions. Every per-candidate
// admin verb was keyed by :userId with no discovery route until this.
const ReviewerRailAdmin = lazy(() => import('../modules/admin-infra/pages/ReviewerRailAdmin.jsx'));
// RV3's seeded documents and the appeals against their scores. Both
// verbs were keyed by an id nothing handed out.
const CalibrationAdmin = lazy(() => import('../modules/admin-infra/pages/CalibrationAdmin.jsx'));
// Attempts the scorer held for a person to decide. The queue and the decision
// existed only as API routes.
const CalibrationHeldAdmin = lazy(() => import('../modules/admin-infra/pages/CalibrationHeldAdmin.jsx'));
const ScoringWeights    = lazy(() => import('../modules/admin-infra/pages/ScoringWeights.jsx'));

/**
 * @function routes
 * @description 
 * Converts the JSON catalog from Service #4 into a secure React routing table.
 * Implements a hydration guard to prevent premature redirects.
 */

// A guest project page for something that is not a project id. Project ids are
// 24-character hex strings minted by the projects service; anything else
// (a typo, a probe, a 500-character string) used to open an interview screen
// and START a project under that text (G-293..296). It is the ordinary 404.
const isProjectId = (id) => /^[a-f0-9]{24}$/i.test(String(id || ''));
const GuestProjectRoute = ({ children }) => {
  const { id } = useParams();
  return isProjectId(id) ? children : <CmsPage />;
};

// The first guest interview UI lived at /guest/requirements/:id and was
// replaced by the v2 page; the old route still served the old page, so a
// visitor could land in either half-version of the same interview (G-297).
const LegacyGuestRedirect = () => {
  const { id } = useParams();
  const { search } = useLocation();
  return isProjectId(id) ? <Navigate to={`/guest/requirements/v2/${id}${search}`} replace /> : <CmsPage />;
};

// A page that moved under a new prefix: keep the rest of the path and the
// query, so /downloads/control/history/abc lands on /downloads/history/abc.
const MovedPrefix = ({ from, to }) => {
  const { pathname, search } = useLocation();
  return <Navigate to={`${to}${pathname.slice(from.length)}${search}`} replace />;
};

export const routes = () => {
  const catalog = useDynamicCatalog();

  // 1. Static Public Routes (Identity Gateway)
  const publicRoutes = [
    { path: '/', element: <GuestLayout><MainDashboard /></GuestLayout> },
    // Guest conversation page renders Claude-clean ChatLayout that owns
    // the full screen — DO NOT wrap in GuestLayout (would add a second
    // header + footer and produce the double-chrome problem).
    { path: '/guest/requirements/:id', element: <LegacyGuestRedirect /> },
    // Guest variant of the v2 surface — same RequirementsV2 page, no auth gate.
    // The v2 service accepts guest tokens for start/answer/read operations.
    { path: '/guest/requirements/v2/:id', element: <GuestProjectRoute><RequirementsV2 /></GuestProjectRoute> },
    // Guest read-only solution report rendered from the v2 answers.
    { path: '/guest/requirements/v2/:id/report', element: <GuestProjectRoute><RequirementsReport /></GuestProjectRoute> },
    // Guest "my reviews" — gated behind project ownership inside the page.
    { path: '/guest/requirements/v2/:id/reviews', element: <GuestProjectRoute><MyReviewsPage /></GuestProjectRoute> },
    { path: '/guest/requirements/v2/:id/project', element: <GuestProjectRoute><ProjectRecord /></GuestProjectRoute> },
    // Public "share for review" report. Owns its own header/footer (like the
    // guest report page), so it is NOT wrapped in GuestLayout — the share token
    // in ?t= is the access grant, validated server-side. Fixes B2 (the guest
    // share link previously 404'd against a non-existent /p/:slug SPA route).
    { path: '/p/:slug', element: <PublicReport /> },
    // The magic link in a review-invitation email. Owns its own chrome like
    // the public report page; opening needs no account, accepting needs the
    // account the invitation was addressed to.
    { path: '/invite/:token', element: <InviteLanding /> },
    { path: '/proposals/claim/:token', element: <ClaimProposal /> },
    { path: '/proposals/unsubscribe/:token', element: <UnsubscribeProposal /> },
    // There is no standalone /projects/:id page — dashboard tiles and other
    // surfaces linked here and 404'd. Redirect to the canonical project surface
    // at /requirements/v2/:id, which reflects interview-in-progress vs finalised
    // state (and offers Preview/Finalise when done).
    // A project has a page of its own now. This used to redirect straight
    // to the interview — better than the 404 before it, but it meant a
    // project had no page, so a link or a bookmark dropped you into the
    // middle of a conversation with no idea what state the work was in.
    // The interview is still the primary action on that page.
    { path: '/projects/:id', element: <ProtectedRoute><Layout><ProjectDetail /></Layout></ProtectedRoute> },
    { path: '/projects/:id/preview', element: <Navigate to="technical" replace /> },
    { path: '/projects/:id/preview/:previewType', element: <GuestLayout><ProjectPreview /></GuestLayout> },
    // The ranked reviewer directory. PUBLIC, matching its gateway guard
    // (authorizeAny) and the reason for it: choosing a reviewer is part of
    // deciding whether to pay, and a guest holding a fresh report is exactly
    // who needs it. Behind ProtectedRoute it would bounce them to /login —
    // the one visitor the page exists for. It publishes only what a reviewer
    // advertises, the same class of information as the public profile below.
    { path: '/reviewers', element: <GuestLayout><ReviewerDirectoryPage /></GuestLayout> },
    { path: '/people/:id', element: <GuestLayout><PublicProfile kind="user" /></GuestLayout> },
    { path: '/organisations/:id', element: <GuestLayout><PublicProfile kind="org" /></GuestLayout> },
    { path: '/organisations/slug/:slug', element: <GuestLayout><PublicProfile kind="org" /></GuestLayout> },
    { path: '/error/500', element: <GuestLayout><InternalServerError /></GuestLayout> },
    { path: '/login', element: <Login /> },
    { path: '/register', element: <Register /> },
    // Public by definition — the visitor is not signed in yet. The redirect
    // URI registered at each provider must point here, not at the API.
    { path: '/auth/callback/:provider', element: <ExternalCallback /> },
    { path: '/password-reset/request', element: <PasswordResetRequest /> },
    { path: '/password-reset/confirm', element: <PasswordResetConfirm /> },
    { path: '/verify-email', element: <VerifyEmail /> },
    // Public example library. The front-page cards point here, so this must
    // stay reachable without auth — a showcase that 404s for logged-out
    // visitors is worse than no showcase.
    { path: '/examples/:exampleCode', element: <GuestLayout><ExampleDetail /></GuestLayout> },
    { path: '/about', element: <GuestLayout><About /></GuestLayout> },
    { path: '/pricing', element: <GuestLayout><Pricing /></GuestLayout> },
    // The integration catalogue: one page per app, prerendered for crawlers
    // (vite-plugins/prerenderPages.js reads the same catalogue).
    { path: '/integrations', element: <GuestLayout><IntegrationsDirectory /></GuestLayout> },
    { path: '/integrations/:slug', element: <GuestLayout><IntegrationPage /></GuestLayout> },
    { path: '/become-a-reviewer', element: <GuestLayout><BecomeReviewer /></GuestLayout> },
    // One onboarding per role × level, before any account: two taps, and the
    // pay, minutes, scope and checker are worked out from the workforce tables.
    { path: '/become-a-reviewer/start', element: <GuestLayout><ReviewerOnboardingStart /></GuestLayout> },
    { path: '/reviewer-pricing', element: <GuestLayout><ReviewerPricing /></GuestLayout> },
    ...DEV_ONLY_ROUTES,
    { path: '/help', element: <GuestLayout><HelpCenter /></GuestLayout> },
    { path: '/search', element: <GuestLayout><SearchResults /></GuestLayout> },
    // Legal documents are content-managed pages, not routes — they are served
    // by the catch-all from the dictionary, like every other /legal/* document.
  ];

  // Core authenticated routes must remain available even when dictionary navigation is not hydrated yet.
  const foundationalPrivateRoutes = [
    // The job pool — real job posts the team runs through Moksy. The service
    // (Private: every entry is behind ProtectedRoute and the gateway's
    // `authorize`. It sat in publicRoutes, which told the signed-out nav and
    // the 401 handler these were public pages.)
    // decides who is on a sourcing team; everyone else sees why not.
    { path: '/sourcing', element: <ProtectedRoute><Layout><SourcingPool /></Layout></ProtectedRoute> },
    // A run over a pool job, and the issue log with its batches (real-jobs brief §10, §12).
    { path: '/sourcing/runs/:runId', element: <ProtectedRoute><Layout><PoolRunPage /></Layout></ProtectedRoute> },
    { path: '/sourcing/issues', element: <ProtectedRoute><Layout><PoolIssuesPage /></Layout></ProtectedRoute> },
    {
      path: '/welcome',
      element: (
        <ProtectedRoute>
          <Layout>
            <WelcomeOnboarding />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/profile',
      element: (
        <ProtectedRoute>
          <Layout>
            <UserProfile />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/purchases',
      element: (
        <ProtectedRoute>
          <Layout>
            <PurchasesWorkspace />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // The reviewer progression ladder. Reviewer-only: it describes the
      // 20-rung reviewer ladder, and a customer seeing it was reading somebody
      // else's career path presented as their own. Non-reviewers go to the
      // application rather than a locked door — that is the thing they would
      // actually want next.
      path: '/ladder',
      element: (
        <ProtectedRoute roles={['reviewer', 'lead_reviewer']} fallback="/reviewer">
          <Layout>
            <MyLadder />
          </Layout>
        </ProtectedRoute>
      )
    },
    // 🏛️ The Commons: the page is public by class (ownership plan §2); the
    // window and a person's units are theirs.
    { path: '/commons', element: <GuestLayout><CommonsPage /></GuestLayout> },
    { path: '/commons/window', element: (<ProtectedRoute><Layout><CommonsWindowPage /></Layout></ProtectedRoute>) },
    { path: '/commons/me', element: (<ProtectedRoute><Layout><CommonsMePage /></Layout></ProtectedRoute>) },
    { path: '/commons/governance', element: (<ProtectedRoute><Layout><CommonsGovernancePage /></Layout></ProtectedRoute>) },
    { path: '/earn', element: (<ProtectedRoute><Layout><EarnGuidePage /></Layout></ProtectedRoute>) },
    {
      path: '/wallet',
      element: (
        <ProtectedRoute>
          <Layout>
            <MyMoxWallet />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/concierge/my-requests',
      element: (
        <ProtectedRoute>
          <Layout>
            <MyConciergeRequests />
          </Layout>
        </ProtectedRoute>
      )
    },
    // Note: legacy single-screen `/jobs/control` (ProtectedRoute) and the
    // standalone `*Control` admin routes have been retired. The 14 service
    // modules below use the canonical `/admin/<svc>/control[/<entity>[/:id]]`
    // pattern driven by `<Service>Home/List/Detail` shells.

    // ── Authenticated conversation page (full-screen, no chrome) ────
    // Same Claude-clean shell as the guest path. Bypasses the global
    // Layout (sidebar + top bar) so the ChatLayout owns the viewport.
    // Pre-empts the dynamic catalog version of the same path.
    {
      path: '/requirements/:id',
      element: (<ProtectedRoute><RequirementsAI /></ProtectedRoute>),
    },

    // Admin URL alias — ProjectList builds this path for admin users
    // (`/admin/requirements/<id>`) but the legacy chat has no admin-specific
    // behaviour, it just uses the same RequirementsAI page. Without this
    // alias, admins clicking the conversation icon hit a SPA 404.
    {
      path: '/admin/requirements/:id',
      element: (<ProtectedRoute><RequirementsAI /></ProtectedRoute>),
    },

    // v2 surface — deterministic catalog-driven engine. Side-by-side with
    // the legacy chat UX above so they can be compared and switched at
    // leisure. Same auth gating as the legacy path.
    {
      path: '/requirements/v2/:id',
      element: (<ProtectedRoute><RequirementsV2 /></ProtectedRoute>),
    },
    {
      // Authenticated solution-outline report. Same component as the
      // guest variant — page-level guest detection is path-based.
      path: '/requirements/v2/:id/report',
      element: (<ProtectedRoute><RequirementsReport /></ProtectedRoute>),
    },
    {
      // The project record — the centre of everything, on one page
      // (docs/MOKSY_PROJECT_ENTITY_MODEL.md §9 item 11).
      path: '/requirements/v2/:id/project',
      element: (<ProtectedRoute><Layout><ProjectRecord /></Layout></ProtectedRoute>),
    },
    {
      path: '/requirements/v2/:id/reviews',
      element: (<ProtectedRoute><Layout><MyReviewsPage /></Layout></ProtectedRoute>),
    },
    // Top-level "all my review requests across every project" view.
    // Same component as the project-scoped path above; the page
    // detects no `:id` and switches to all-mine mode (fetches via
    // RequirementsV2API.myReviews, hides the per-project invites tab).
    {
      path: '/reviews',
      element: (<ProtectedRoute><Layout><MyReviewsPage /></Layout></ProtectedRoute>),
    },

    // Customer landing console — analogue of OperatorConsole for the
    // user track. Mounted at /home and surfaced as the top sidebar
    // entry in the Workspace section (USER_TRACK_ROLES only).
    {
      path: '/home',
      element: (<ProtectedRoute><Layout><CustomerHome /></Layout></ProtectedRoute>),
    },

    // Per-user notification preferences — channel + event toggles.
    // Visible to every authenticated user (operators also have
    // preferences against their staff identity).
    {
      path: '/settings/notifications',
      element: (<ProtectedRoute><Layout><NotificationPreferences /></Layout></ProtectedRoute>),
    },

    // GDPR self-service — export your data (Art. 20) and request erasure
    // (Art. 17). Every authenticated user; the backend scopes to the caller.
    {
      path: '/settings/my-data',
      element: (<ProtectedRoute><Layout><MyDataPage /></Layout></ProtectedRoute>),
    },

    // Developer surface: keys, webhooks, contract, limits. Every authenticated
    // user; the backend scopes keys and webhooks to the caller.
    {
      path: '/settings/developer',
      element: (<ProtectedRoute><Layout><DeveloperPage /></Layout></ProtectedRoute>),
    },

    // Customer-facing unified message inbox — the customer↔reviewer chat
    // threads that back paid solution documents. Any authenticated user;
    // the comms endpoints scope threads to the caller's participation.
    {
      path: '/messages',
      element: (<ProtectedRoute><Layout><MessagesInbox /></Layout></ProtectedRoute>),
    },

    // Stripe Checkout landing — public route (Stripe redirects come
    // back through here regardless of session validity; the page
    // re-confirms auth before doing anything).
    {
      path: '/billing/success',
      element: (<ProtectedRoute><Layout><BillingSuccess /></Layout></ProtectedRoute>),
    },
    {
      path: '/billing/cancel',
      element: (<ProtectedRoute><Layout><BillingCancel /></Layout></ProtectedRoute>),
    },

    // ── Operator console (priority #5) ──────────────────────────────
    // Owner-group governance pages. AdminRoute now allows all owner
    // roles (super_admin, admin, manager, finance, support); the
    // per-page action gating (e.g. matrix Save) checks scopes.
    {
      path: '/admin/eligibility-matrix',
      element: (<AdminRoute><Layout><EligibilityMatrix /></Layout></AdminRoute>),
    },
    // AdminRoute gates owner-group at the boundary; the gateway then requires
    // SUPER admin on the config endpoints themselves. The stricter check is
    // the server's — a support operator reaching this page sees it fail to
    // load rather than being able to change what guards the front door.
    {
      path: '/admin/captcha',
      element: (<AdminRoute><Layout><CaptchaSettings /></Layout></AdminRoute>),
    },
    {
      path: '/admin/calibration',
      element: (
        <AdminRoute>
          <Layout>
            <CalibrationSeedsAdmin />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/reviewer-plans',
      element: (
        <AdminRoute>
          <Layout>
            <ReviewerPlansAdmin />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/reviewer-earnings',
      element: (<AdminRoute><Layout><ReviewerEarnings /></Layout></AdminRoute>),
    },
    {
      path: '/admin/commons',
      element: (<AdminRoute><Layout><CommonsConsole /></Layout></AdminRoute>),
    },
    {
      path: '/admin/reviewer-promotions',
      element: (<AdminRoute><Layout><ReviewerPromotions /></Layout></AdminRoute>),
    },
    {
      // Sales, support, partner and job enquiries. The forms that feed it were
      // `mailto:` links until 2026-09-08; a record nobody reads would be worse
      // than the mailto, so the queue shipped with them.
      path: '/admin/enquiries',
      element: (<AdminRoute><Layout><EnquiriesQueue /></Layout></AdminRoute>),
    },
    {
      path: '/admin/reviewer-applications',
      element: (<AdminRoute><Layout><ReviewerRailAdmin /></Layout></AdminRoute>),
    },
    {
      // 🚪 Appeals against a calibration score, on their OWN path (GC-108).
      // This was declared as a second `/admin/calibration`, so it never
      // rendered: React Router matched the seeds screen above it and this
      // console — the only place a contested score can be answered — could
      // not be reached from anywhere, menu included.
      path: '/admin/calibration/appeals',
      element: (<AdminRoute><Layout><CalibrationAdmin /></Layout></AdminRoute>),
    },
    {
      path: '/admin/calibration/held',
      element: (<AdminRoute><Layout><CalibrationHeldAdmin /></Layout></AdminRoute>),
    },
    {
      path: '/admin/scoring-weights',
      element: (<AdminRoute><Layout><ScoringWeights /></Layout></AdminRoute>),
    },
    {
      // Social login configuration. The auth service has stored provider
      // credentials and merged them over the environment for a while; this
      // is the screen that was missing, so switching Google on no longer
      // needs an .env edit and a container recreate.
      path: '/admin/auth-providers',
      element: (<AdminRoute><Layout><AuthProviders /></Layout></AdminRoute>),
    },
    {
      // Read-only view of platform tunables (guest limits, password
      // policy, cookie/HTTPS knobs, masked secrets). Super-admin only —
      // the gateway endpoint enforces; AdminRoute gates owner-group at
      // the route level.
      path: '/admin/platform-settings',
      element: (<AdminRoute><Layout><PlatformSettings /></Layout></AdminRoute>),
    },
    {
      // Operator console — every review request + invitation across
      // the platform, regardless of owner / assignee. Distinct from
      // the per-owner "my reviews" and per-reviewer "queue" surfaces.
      path: '/admin/review-pipeline',
      element: (<AdminRoute><Layout><ReviewPipeline /></Layout></AdminRoute>),
    },

    // Requirements v2 admin console — KPI snapshot + feature flags +
    // audit log + contested-review adjudication + expert application
    // queue. Paths match the cross-links baked into AdminHomePage.jsx.
    {
      path: '/requirements/v2/admin',
      element: (<AdminRoute><Layout><RequirementsV2AdminHome /></Layout></AdminRoute>),
    },
    {
      path: '/admin/reports',
      element: (<AdminRoute><Layout><ReportManager /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/feature-flags',
      element: (<AdminRoute><Layout><RequirementsV2FeatureFlags /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/audit-log',
      element: (<AdminRoute><Layout><RequirementsV2AuditLog /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/disputes',
      element: (<AdminRoute><Layout><RequirementsV2Disputes /></Layout></AdminRoute>),
    },
    // 👥 The two-person rule: the queue, and which capabilities carry it.
    {
      path: '/requirements/v2/admin/two-person',
      element: (<AdminRoute><Layout><TwoPersonApprovals /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/expert-applications',
      element: (<AdminRoute><Layout><RequirementsV2ExpertApplications /></Layout></AdminRoute>),
    },
    {
      // Detail view for one application. The queue row links here; the
      // inline Decide on the queue stays for the obvious cases.
      path: '/requirements/v2/admin/expert-applications/:applicationId',
      element: (<AdminRoute><Layout><ExpertApplicationDetail /></Layout></AdminRoute>),
    },

    // Content hierarchy (Phase 1 — data model + admin CRUD only; not
    // yet wired into real document generation, see PillarSeedService.js).
    {
      path: '/requirements/v2/admin/pillars',
      element: (<AdminRoute><Layout><RequirementsV2Pillars /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/sub-pillars',
      element: (<AdminRoute><Layout><RequirementsV2SubPillars /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/requirements-catalog',
      element: (<AdminRoute><Layout><RequirementsV2RequirementsCatalog /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/base-template',
      element: (<AdminRoute><Layout><RequirementsV2BaseTemplate /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/master-template',
      element: (<AdminRoute><Layout><RequirementsV2MasterTemplate /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/document-category-templates',
      element: (<AdminRoute><Layout><RequirementsV2DocCategoryTemplates /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/template-proposals',
      element: (<AdminRoute><Layout><RequirementsV2TemplateProposals /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/base-solution-document',
      element: (<AdminRoute><Layout><RequirementsV2BaseSolutionDocument /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/experiment',
      element: (<AdminRoute><Layout><RequirementsV2Experiment /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/experiment-runs',
      element: (<AdminRoute><Layout><RequirementsV2ExperimentRuns /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/workflow-version',
      element: (<AdminRoute><Layout><RequirementsV2WorkflowVersion /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/workflow-templates',
      element: (<AdminRoute><Layout><RequirementsV2WorkflowTemplates /></Layout></AdminRoute>),
    },
    {
      // Queue health for staff — what is waiting and what is late. Not a
      // scoreboard; see the page header for why.
      path: '/requirements/v2/admin/queues',
      element: (<AdminRoute><Layout><StaffQueuesPage /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/journeys',
      element: (<AdminRoute><Layout><RequirementsV2Journeys /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/who-was-wrong',
      element: (<AdminRoute><Layout><RequirementsV2WhoWasWrong /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/examples',
      element: (<AdminRoute><Layout><RequirementsV2Examples /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/review',
      element: (<AdminRoute><Layout><RequirementsV2Review /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/proposal-clusters',
      element: (<AdminRoute><Layout><RequirementsV2ProposalClusters /></Layout></AdminRoute>),
    },
    {
      // What a project can carry beyond the seventy fields the platform
      // declares in code. Beside Schemas because it is the same question —
      // "what shape is this data?" — for the one model that can answer it
      // without a deploy.
      path: '/knowledge/project-fields',
      element: (<AdminRoute><Layout><ProjectFields /></Layout></AdminRoute>),
    },
    {
      // How each kind of record is DEFINED, as opposed to the records
      // themselves. Every Knowledge Base form is generated from these, and
      // until now the only way to read one was the registry's source.
      path: '/knowledge/schemas',
      element: (<AdminRoute><Layout><Schemas /></Layout></AdminRoute>),
    },
    {
      // The same screen with one schema open. A route rather than a state flag
      // so a schema can be linked to, opened in a tab, and left with the
      // browser's own Back — none of which an inline panel allows.
      path: '/knowledge/schemas/:code',
      element: (<AdminRoute><Layout><Schemas /></Layout></AdminRoute>),
    },
    {
      // Data — the corpus filed by lifecycle domain, which is the axis
      // somebody actually browses it on. The per-type screens below still own
      // editing; this is how you FIND the record to edit.
      path: '/knowledge/data',
      element: (<AdminRoute><Layout><DataHome /></Layout></AdminRoute>),
    },
    {
      // The corpus itself — hosted by the dictionary service alongside the
      // controlled vocabulary. Distinct from the page below, which edits the
      // question defaults and building blocks the requirements service owns.
      path: '/knowledge/corpus',
      element: (<AdminRoute><Layout><KnowledgeCorpus /></Layout></AdminRoute>),
    },
    {
      // Every report type and the structure it produces. Replaces the Examples
      // page in the menu; that route stays registered so nothing is destroyed
      // and the coverage view remains reachable.
      path: '/knowledge/output-reports',
      element: (<AdminRoute><Layout><OutputReports /></Layout></AdminRoute>),
    },
    {
      // A category, and the reports in it. `/stage/` rather than a bare
      // `/:lifecycle` so it can never be confused with a report code — two
      // sibling dynamic segments under one parent is a shadowing bug waiting
      // to be written.
      path: '/knowledge/output-reports/stage/:lifecycle',
      element: (<AdminRoute><Layout><OutputReportCategory /></Layout></AdminRoute>),
    },
    {
      // One report, at its own address. It was a right-hand pane, which cannot
      // be linked to or reloaded onto — and "have a look at the Feasibility
      // one" is the commonest thing anybody says about a catalogue of 56.
      path: '/knowledge/output-reports/type/:reportTypeCode',
      element: (<AdminRoute><Layout><OutputReportDetail /></Layout></AdminRoute>),
    },
    {
      // Every tunable decision the platform makes. Its own top-level section
      // rather than under Knowledge Base: patterns act ON knowledge, they are
      // not a kind of it.
      path: '/patterns',
      element: (<AdminRoute><Layout><Patterns /></Layout></AdminRoute>),
    },
    {
      // Every action the platform can perform. Patterns is this list narrowed
      // to the tunable half, which is why they sit in one section.
      path: '/functions',
      element: (<AdminRoute><Layout><Functions /></Layout></AdminRoute>),
    },
    {
      // Named, versioned workflows — each owning its own step tree with a
      // function and parameter values bound per step. Not a fourth content
      // tier: Base/Master/Category overlay VALUES onto one shared pillar tree
      // and answer what a report contains; this answers which steps run.
      path: '/templates',
      element: (<AdminRoute><Layout><Templates /></Layout></AdminRoute>),
    },
    {
      path: '/templates/:code',
      element: (<AdminRoute><Layout><TemplateDetail /></Layout></AdminRoute>),
    },
    {
      // Which workflow a new project runs, and why. One global switch before
      // this, which is why two published templates existed and only one could
      // ever be reached.
      path: '/configurations',
      element: (<AdminRoute><Layout><Configurations /></Layout></AdminRoute>),
    },
    {
      // The one input the loop does not have. Every stage downstream of it is
      // built and produces nothing: zero proposal clusters from four
      // corrections, metrics computed from eleven ratings, no gold at all.
      // Judging was only ever possible from a table that showed a run and
      // offered no way to rate it.
      // Every run and what was concluded about it. The judging queue below is
      // one run at a time and hardcoded to `unjudged`, so until this existed
      // the judged, corrected and failed runs were unreachable from anywhere.
      path: '/learning/runs',
      element: (<AdminRoute><Layout><LearningRuns /></Layout></AdminRoute>),
    },
    {
      path: '/learning/judge',
      element: (<AdminRoute><Layout><JudgingQueue /></Layout></AdminRoute>),
    },
    {
      // What the loop concluded, from twenty thousand snapshots a nightly job
      // had been writing with nothing on the other end to read them.
      path: '/learning/metrics',
      element: (<AdminRoute><Layout><LearningMetrics /></Layout></AdminRoute>),
    },
    {
      // Which approved examples an idea pulls into the prompt, and the exact
      // text injected because of them.
      path: '/learning/retrieval',
      element: (<AdminRoute><Layout><RetrievalPreview /></Layout></AdminRoute>),
    },
    {
      // One screen per entity type. The type is in the URL so it is
      // addressable — a curator works in Protocols, not in "the knowledge
      // base", and wants to be able to send somebody the link.
      path: '/knowledge/type/:entityType',
      element: (<AdminRoute><Layout><KnowledgeType /></Layout></AdminRoute>),
    },
    {
      // Renamed to Knowledge Base — the universal pool, as distinct from
      // Evidence (corpus/runs/corrections). The old path stays registered
      // below so existing links and bookmarks keep working.
      path: '/requirements/v2/admin/knowledge-base',
      element: (<AdminRoute><Layout><RequirementsV2DataManagement /></Layout></AdminRoute>),
    },
    {
      // Legacy path, kept so existing links keep working.
      path: '/requirements/v2/admin/data-management',
      element: (<AdminRoute><Layout><RequirementsV2DataManagement /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/factor-weights',
      element: (<AdminRoute><Layout><RequirementsV2FactorWeights /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/report-format',
      element: (<AdminRoute><Layout><RequirementsV2ReportFormat /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/report-structure',
      element: (<AdminRoute><Layout><RequirementsV2ReportStructure /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/master-solution-document',
      element: (<AdminRoute><Layout><RequirementsV2MasterSolutionDocument /></Layout></AdminRoute>),
    },
    {
      path: '/requirements/v2/admin/category-solution-document',
      element: (<AdminRoute><Layout><RequirementsV2CategorySolutionDocument /></Layout></AdminRoute>),
    },

    // ── Operator console landing ───────────────────────────────────────
    // Pulls together "what needs attention" across the operator
    // surfaces. The sidebar's Operator section now points here as the
    // section root; individual leaf surfaces are still reachable
    // directly as before.
    {
      path: '/admin/console',
      element: (<AdminRoute><Layout><OperatorConsole /></Layout></AdminRoute>),
    },

    // Payments module — Overview is the admin landing inside this
    // service; the four sub-pages share a tab nav inside PageShell so
    // operators can move between them without hitting the sidebar.
    // AdminRoute gates owner-group at the boundary; per-action
    // permissions (refund, activate provider, save credentials) are
    // enforced server-side and surfaced through hidden buttons here.
    {
      path: '/billing/payments',
      element: (<AdminRoute><Layout><PaymentsHome /></Layout></AdminRoute>),
    },
    {
      path: '/admin/payments/transactions',
      element: (<AdminRoute><Layout><PaymentsTransactions /></Layout></AdminRoute>),
    },
    {
      path: '/admin/payments/disputes',
      element: (<AdminRoute><Layout><PaymentsDisputes /></Layout></AdminRoute>),
    },
    {
      path: '/admin/payments/providers',
      element: (<AdminRoute><Layout><PaymentsProviders /></Layout></AdminRoute>),
    },
    {
      path: '/admin/payments/settings',
      element: (<AdminRoute><Layout><PaymentSettings /></Layout></AdminRoute>),
    },
    {
      path: '/admin/auth/providers',
      element: (<AdminRoute><Layout><IdentityProviderSettings /></Layout></AdminRoute>),
    },
    // Review marketplace policy — operator controls (kill-switch,
    // caps, earnings ceilings, section-eligibility matrix).
    {
      path: '/admin/reviews/settings',
      element: (<AdminRoute><Layout><ReviewPolicy /></Layout></AdminRoute>),
    },
    // Customer-facing personal transactions.
    //
    // This used to mount MyTransactions, which lists payment transactions
    // only. The billing hub's Transactions tab lists the same feed merged
    // with the MOX ledger — a strict superset — so a member had two
    // transaction histories in the nav, side by side, that disagreed about
    // what had happened to their money. The path stays (bookmarks, the link
    // on member home, the payments console) and lands on the complete one.
    {
      path: '/billing/transactions',
      element: <Navigate to="/billing?tab=transactions" replace />,
    },

    // ── Users service admin surface ────────────────────────────────────
    // Three coordinated pages: the stats dashboard, the per-user
    // console (IdentityConsole, already mounted via catalog), and the
    // global settings panel. Keeping them under /admin/users-admin/*
    // avoids colliding with the catalog-driven /admin/users path.
    {
      path: '/admin/users-admin',
      element: (<AdminRoute><Layout><UsersAdminDashboard /></Layout></AdminRoute>),
    },
    {
      path: '/admin/users-admin/settings',
      element: (<AdminRoute><Layout><UsersAdminSettings /></Layout></AdminRoute>),
    },

    // ── Audit (3-screen reference implementation) ────────────────────
    // Pattern: <basePath> = Home, <basePath>/<entity> = List,
    //          <basePath>/<entity>/:id = Detail. Driven by audit.config.js.
    {
      path: '/admin/audit/control',
      element: (<AdminRoute><Layout><AuditHome /></Layout></AdminRoute>),
    },
    {
      path: '/admin/audit/control/entries',
      element: (<AdminRoute><Layout><AuditList entity="entries" /></Layout></AdminRoute>),
    },
    {
      path: '/admin/audit/control/entries/:id',
      element: (<AdminRoute><Layout><AuditDetail entity="entries" /></Layout></AdminRoute>),
    },
    {
      path: '/admin/requirements/assessment-taxonomy',
      element: (<AdminRoute><Layout><AssessmentTaxonomyPage /></Layout></AdminRoute>),
    },
    {
      path: '/admin/audit/control/suspicious',
      element: (<AdminRoute><Layout><AuditList entity="suspicious" /></Layout></AdminRoute>),
    },
    {
      path: '/admin/audit/control/integrity',
      element: (<AdminRoute><Layout><AuditList entity="integrity" /></Layout></AdminRoute>),
    },

    // ── Events (3-screen pattern) ────────────────────────────────────
    { path: '/admin/events/control',
      element: (<AdminRoute><Layout><EventsHome /></Layout></AdminRoute>) },
    { path: '/admin/events/control/subscriptions',
      element: (<AdminRoute><Layout><EventsList entity="subscriptions" /></Layout></AdminRoute>) },
    { path: '/admin/events/control/subscriptions/:id',
      element: (<AdminRoute><Layout><EventsDetail entity="subscriptions" /></Layout></AdminRoute>) },
    { path: '/admin/events/control/webhooks',
      element: (<AdminRoute><Layout><EventsList entity="webhooks" /></Layout></AdminRoute>) },
    { path: '/admin/events/control/webhooks/:id',
      element: (<AdminRoute><Layout><EventsDetail entity="webhooks" /></Layout></AdminRoute>) },
    { path: '/admin/events/control/types',
      element: (<AdminRoute><Layout><EventsList entity="types" /></Layout></AdminRoute>) },
    { path: '/admin/events/control/types/:id',
      element: (<AdminRoute><Layout><EventsDetail entity="types" /></Layout></AdminRoute>) },
    { path: '/admin/events/control/failed',
      element: (<AdminRoute><Layout><EventsList entity="failed" /></Layout></AdminRoute>) },
    { path: '/admin/events/control/failed/:id',
      element: (<AdminRoute><Layout><EventsDetail entity="failed" /></Layout></AdminRoute>) },

    // ── Notifications (3-screen pattern) ─────────────────────────────
    { path: '/admin/notifications/control',
      element: (<AdminRoute><Layout><NotificationsHome /></Layout></AdminRoute>) },
    { path: '/admin/notifications/control/inbox',
      element: (<AdminRoute><Layout><NotificationsList entity="inbox" /></Layout></AdminRoute>) },
    { path: '/admin/notifications/control/inbox/:id',
      element: (<AdminRoute><Layout><NotificationsDetail entity="inbox" /></Layout></AdminRoute>) },
    { path: '/admin/notifications/control/broadcasts',
      element: (<AdminRoute><Layout><NotificationsList entity="broadcasts" /></Layout></AdminRoute>) },
    { path: '/admin/notifications/control/logs',
      element: (<AdminRoute><Layout><NotificationsList entity="logs" /></Layout></AdminRoute>) },
    { path: '/admin/notifications/control/failures',
      element: (<AdminRoute><Layout><NotificationsList entity="failures" /></Layout></AdminRoute>) },
    { path: '/admin/notifications/control/mappings',
      element: (<AdminRoute><Layout><NotificationsList entity="mappings" /></Layout></AdminRoute>) },

    // ── Jobs (3-screen pattern) ──────────────────────────────────────
    { path: '/admin/jobs/control',
      element: (<AdminRoute><Layout><JobsHome /></Layout></AdminRoute>) },
    { path: '/admin/jobs/control/mine',
      element: (<AdminRoute><Layout><JobsList entity="mine" /></Layout></AdminRoute>) },
    { path: '/admin/jobs/control/mine/:id',
      element: (<AdminRoute><Layout><JobsDetail entity="mine" /></Layout></AdminRoute>) },
    { path: '/admin/jobs/control/failed',
      element: (<AdminRoute><Layout><JobsList entity="failed" /></Layout></AdminRoute>) },
    { path: '/admin/jobs/control/failed/:id',
      element: (<AdminRoute><Layout><JobsDetail entity="failed" /></Layout></AdminRoute>) },
    { path: '/admin/jobs/control/queues',
      element: (<AdminRoute><Layout><JobsList entity="queues" /></Layout></AdminRoute>) },
    { path: '/admin/jobs/control/workers',
      element: (<AdminRoute><Layout><JobsList entity="workers" /></Layout></AdminRoute>) },
    { path: '/admin/jobs/control/schedules',
      element: (<AdminRoute><Layout><JobsList entity="schedules" /></Layout></AdminRoute>) },
    // Recurring work that runs on in-process timers rather than through the
    // queue. Separate from `schedules`, which lists cron entries registered
    // via the API — these are the ones nothing was watching.
    { path: '/admin/jobs/control/loops',
      element: (<AdminRoute><Layout><JobsList entity="loops" /></Layout></AdminRoute>) },

    // ── Orchestrator (3-screen pattern) ──────────────────────────────
    { path: '/admin/orchestrator/control',
      element: (<AdminRoute><Layout><OrchestratorHome /></Layout></AdminRoute>) },
    { path: '/admin/orchestrator/control/workflows',
      element: (<AdminRoute><Layout><OrchestratorList entity="workflows" /></Layout></AdminRoute>) },
    { path: '/admin/orchestrator/control/workflows/:id',
      element: (<AdminRoute><Layout><OrchestratorDetail entity="workflows" /></Layout></AdminRoute>) },
    { path: '/admin/orchestrator/control/strategies',
      element: (<AdminRoute><Layout><OrchestratorList entity="strategies" /></Layout></AdminRoute>) },
    { path: '/admin/orchestrator/control/strategies/:id',
      element: (<AdminRoute><Layout><OrchestratorDetail entity="strategies" /></Layout></AdminRoute>) },
    { path: '/admin/orchestrator/control/traces',
      element: (<AdminRoute><Layout><OrchestratorList entity="traces" /></Layout></AdminRoute>) },

    // ── Registry (3-screen pattern) ──────────────────────────────────
    { path: '/admin/registry/control',
      element: (<AdminRoute><Layout><RegistryHome /></Layout></AdminRoute>) },
    { path: '/admin/registry/control/catalog',
      element: (<AdminRoute><Layout><RegistryList entity="catalog" /></Layout></AdminRoute>) },
    { path: '/admin/registry/control/catalog/:id',
      element: (<AdminRoute><Layout><RegistryDetail entity="catalog" /></Layout></AdminRoute>) },
    { path: '/admin/registry/control/templates',
      element: (<AdminRoute><Layout><RegistryList entity="templates" /></Layout></AdminRoute>) },
    { path: '/admin/registry/control/trending',
      element: (<AdminRoute><Layout><RegistryList entity="trending" /></Layout></AdminRoute>) },
    { path: '/admin/registry/control/conflicts',
      element: (<AdminRoute><Layout><RegistryList entity="conflicts" /></Layout></AdminRoute>) },

    // ── Communications (3-screen pattern) ────────────────────────────
    { path: '/admin/communications/control',
      element: (<AdminRoute><Layout><CommunicationsHome /></Layout></AdminRoute>) },
    { path: '/admin/communications/control/threads',
      element: (<AdminRoute><Layout><CommunicationsList entity="threads" /></Layout></AdminRoute>) },
    { path: '/admin/communications/control/threads/:id',
      element: (<AdminRoute><Layout><CommunicationsDetail entity="threads" /></Layout></AdminRoute>) },
    { path: '/admin/communications/control/templates',
      element: (<AdminRoute><Layout><CommunicationsList entity="templates" /></Layout></AdminRoute>) },
    { path: '/admin/communications/control/templates/:id',
      element: (<AdminRoute><Layout><CommunicationsDetail entity="templates" /></Layout></AdminRoute>) },
    { path: '/admin/communications/control/broadcasts',
      element: (<AdminRoute><Layout><CommunicationsList entity="broadcasts" /></Layout></AdminRoute>) },
    { path: '/admin/communications/control/providers',
      element: (<AdminRoute><Layout><CommunicationsList entity="providers" /></Layout></AdminRoute>) },
    // Bespoke credential configurator (SMTP/Mailgun/SendGrid/Twilio) —
    // same split as payments: the generic list above stays read-only,
    // this hand-registered page owns the encrypted-credentials CRUD.
    { path: '/admin/communications/control/settings',
      element: (<AdminRoute><Layout><CommunicationsSettings /></Layout></AdminRoute>) },
    { path: '/admin/communications/control/logs',
      element: (<AdminRoute><Layout><CommunicationsList entity="logs" /></Layout></AdminRoute>) },

    // ── Payments (3-screen pattern) ──────────────────────────────────
    { path: '/admin/payments/control',
      element: (<AdminRoute><Layout><PaymentsHome /></Layout></AdminRoute>) },
    { path: '/admin/payments/control/transactions',
      element: (<AdminRoute><Layout><PaymentsList entity="transactions" /></Layout></AdminRoute>) },
    { path: '/admin/payments/control/transactions/:id',
      element: (<AdminRoute><Layout><PaymentsDetail entity="transactions" /></Layout></AdminRoute>) },
    { path: '/admin/payments/control/invoices',
      element: (<AdminRoute><Layout><PaymentsList entity="invoices" /></Layout></AdminRoute>) },
    { path: '/admin/payments/control/methods',
      element: (<AdminRoute><Layout><PaymentsList entity="methods" /></Layout></AdminRoute>) },
    { path: '/admin/payments/control/products',
      element: (<AdminRoute><Layout><PaymentsList entity="products" /></Layout></AdminRoute>) },
    { path: '/admin/payments/control/providers',
      element: (<AdminRoute><Layout><PaymentsList entity="providers" /></Layout></AdminRoute>) },
    { path: '/admin/payments/control/disputes',
      element: (<AdminRoute><Layout><PaymentsList entity="disputes" /></Layout></AdminRoute>) },

    // ── Subscriptions / Billing (3-screen pattern) ───────────────────
    { path: '/admin/subscriptions/control',
      element: (<AdminRoute><Layout><SubscriptionsHome /></Layout></AdminRoute>) },
    { path: '/admin/subscriptions/control/plans',
      element: (<AdminRoute><Layout><SubscriptionsList entity="plans" /></Layout></AdminRoute>) },
    { path: '/admin/subscriptions/control/plans/:id',
      element: (<AdminRoute><Layout><SubscriptionsDetail entity="plans" /></Layout></AdminRoute>) },
    { path: '/admin/subscriptions/control/history',
      element: (<AdminRoute><Layout><SubscriptionsList entity="history" /></Layout></AdminRoute>) },
    { path: '/admin/subscriptions/control/periods',
      element: (<AdminRoute><Layout><SubscriptionsList entity="periods" /></Layout></AdminRoute>) },
    { path: '/admin/subscriptions/control/authorizations',
      element: (<AdminRoute><Layout><SubscriptionsList entity="authorizations" /></Layout></AdminRoute>) },
    { path: '/admin/subscriptions/control/unreconciled',
      element: (<AdminRoute><Layout><SubscriptionsList entity="unreconciled" /></Layout></AdminRoute>) },

    // ── Downloads (3-screen pattern) ─────────────────────────────────
    // The MEMBER-facing downloads view. The Workspace nav has offered
    // "Downloads" to every signed-in person since it was written, pointing at
    // /downloads/control — a path with no route, so React Router rendered the
    // error state and a member's first click on Downloads was a broken page.
    // The only downloads UI that existed was the admin console below.
    //
    // At /downloads, not /downloads/control. `/control` is the admin consoles'
    // three-screen suffix, so a customer's own downloads page read as a
    // service control panel in the address bar and in the audit of who is
    // shown what (walked 2026-09-11). The old paths redirect, so a bookmark
    // or an emailed link still lands.
    { path: '/downloads',
      element: (<ProtectedRoute><Layout><DownloadsHome config={downloadsMemberConfig} /></Layout></ProtectedRoute>) },
    { path: '/downloads/history',
      element: (<ProtectedRoute><Layout><DownloadsList entity="history" config={downloadsMemberConfig} /></Layout></ProtectedRoute>) },
    { path: '/downloads/history/:id',
      element: (<ProtectedRoute><Layout><DownloadsDetail entity="history" config={downloadsMemberConfig} /></Layout></ProtectedRoute>) },
    { path: '/downloads/control', element: <MovedPrefix from="/downloads/control" to="/downloads" /> },
    { path: '/downloads/control/*', element: <MovedPrefix from="/downloads/control" to="/downloads" /> },
    { path: '/admin/downloads/control',
      element: (<AdminRoute><Layout><DownloadsHome /></Layout></AdminRoute>) },
    { path: '/admin/downloads/control/history',
      element: (<AdminRoute><Layout><DownloadsList entity="history" /></Layout></AdminRoute>) },
    { path: '/admin/downloads/control/history/:id',
      element: (<AdminRoute><Layout><DownloadsDetail entity="history" /></Layout></AdminRoute>) },
    { path: '/admin/downloads/control/active-links',
      element: (<AdminRoute><Layout><DownloadsList entity="activeLinks" /></Layout></AdminRoute>) },

    // ── Storage (3-screen pattern) ───────────────────────────────────
    { path: '/admin/storage/control',
      element: (<AdminRoute><Layout><StorageHome /></Layout></AdminRoute>) },
    { path: '/admin/storage/control/files',
      element: (<AdminRoute><Layout><StorageList entity="files" /></Layout></AdminRoute>) },
    { path: '/admin/storage/control/files/:id',
      element: (<AdminRoute><Layout><StorageDetail entity="files" /></Layout></AdminRoute>) },
    { path: '/admin/storage/control/flagged',
      element: (<AdminRoute><Layout><StorageList entity="flagged" /></Layout></AdminRoute>) },
    { path: '/admin/storage/control/providers',
      element: (<AdminRoute><Layout><StorageList entity="providers" /></Layout></AdminRoute>) },

    // ── AI LLM (3-screen pattern) ────────────────────────────────────
    { path: '/admin/ai-llm/control',
      element: (<AdminRoute><Layout><AiLlmHome /></Layout></AdminRoute>) },
    // Platform-wide active-provider toggle (super-admin). Drives both
    // ai-llm synthesis AND the requirements conversation flow.
    { path: '/admin/ai-llm/control/active-provider',
      element: (<AdminRoute><Layout><AiLlmActiveProvider /></Layout></AdminRoute>) },
    { path: '/admin/ai-llm/control/providers',
      element: (<AdminRoute><Layout><AiLlmList entity="providers" /></Layout></AdminRoute>) },
    { path: '/admin/ai-llm/control/providers/:id',
      element: (<AdminRoute><Layout><AiLlmDetail entity="providers" /></Layout></AdminRoute>) },
    { path: '/admin/ai-llm/control/models',
      element: (<AdminRoute><Layout><AiLlmList entity="models" /></Layout></AdminRoute>) },
    { path: '/admin/ai-llm/control/routing',
      element: (<AdminRoute><Layout><AiLlmList entity="routing" /></Layout></AdminRoute>) },
    { path: '/admin/ai-llm/control/costs',
      element: (<AdminRoute><Layout><AiLlmList entity="costs" /></Layout></AdminRoute>) },
    { path: '/admin/ai-llm/control/performance',
      element: (<AdminRoute><Layout><AiLlmList entity="performance" /></Layout></AdminRoute>) },

    // ── AI Memory (3-screen pattern) ─────────────────────────────────
    { path: '/admin/ai-memory/control',
      element: (<AdminRoute><Layout><AiMemoryHome /></Layout></AdminRoute>) },
    { path: '/admin/ai-memory/control/namespaces',
      element: (<AdminRoute><Layout><AiMemoryList entity="namespaces" /></Layout></AdminRoute>) },
    { path: '/admin/ai-memory/control/providers',
      element: (<AdminRoute><Layout><AiMemoryList entity="providers" /></Layout></AdminRoute>) },

    // ── AI Policy (3-screen pattern) ─────────────────────────────────
    { path: '/admin/ai-policy/control',
      element: (<AdminRoute><Layout><AiPolicyHome /></Layout></AdminRoute>) },
    { path: '/admin/ai-policy/control/routing-rules',
      element: (<AdminRoute><Layout><AiPolicyList entity="routingRules" /></Layout></AdminRoute>) },
    { path: '/admin/ai-policy/control/denylist',
      element: (<AdminRoute><Layout><AiPolicyList entity="denylist" /></Layout></AdminRoute>) },
    { path: '/admin/ai-policy/control/scrub-rules',
      element: (<AdminRoute><Layout><AiPolicyList entity="scrubRules" /></Layout></AdminRoute>) },
    { path: '/admin/ai-policy/control/audit-decisions',
      element: (<AdminRoute><Layout><AiPolicyList entity="auditDecisions" /></Layout></AdminRoute>) },
    { path: '/admin/ai-policy/control/audit-decisions/:id',
      element: (<AdminRoute><Layout><AiPolicyDetail entity="auditDecisions" /></Layout></AdminRoute>) },
    { path: '/admin/ai-policy/control/versions',
      element: (<AdminRoute><Layout><AiPolicyList entity="versions" /></Layout></AdminRoute>) },

    // ── Organisation routes ──────────────────────────────────────────
    // /orgs/invitations/accept MUST precede /orgs/:id to avoid param collision
    {
      path: '/orgs',
      element: (
        <ProtectedRoute>
          <Layout>
            <OrgManager />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/orgs/invitations/accept',
      element: (
        <ProtectedRoute>
          <Layout>
            <OrgManager />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/orgs/:id',
      element: (
        <ProtectedRoute>
          <Layout>
            <OrgManager />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/orgs/:id/projects',
      element: (
        <ProtectedRoute>
          <Layout>
            <OrgManager />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/orgs/:id/commerce',
      element: (
        <ProtectedRoute>
          <Layout>
            <OrgManager />
          </Layout>
        </ProtectedRoute>
      )
    },

    // ─── Newton solution report (per-project) ───────────────────────
    // /projects/:id/report     — finalises if needed, then redirects with runId
    // /projects/:id/report/:runId — direct render of a specific run's output
    {
      path: '/projects/:projectId/report',
      element: (
        <ProtectedRoute>
          <Layout>
            <SolutionReport />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/projects/:projectId/report/:runId',
      element: (
        <ProtectedRoute>
          <Layout>
            <SolutionReport />
          </Layout>
        </ProtectedRoute>
      )
    },
    // 💳 Unified billing hub — Subscription · Invoices · Payment
    // methods · Billing address · Transactions. Lands at /billing
    // (no slug) so the top-level "Billing" sidebar link goes straight
    // here. Deep-link via ?tab=invoices etc.
    {
      path: '/billing',
      element: (
        <ProtectedRoute>
          <Layout>
            <BillingHub />
          </Layout>
        </ProtectedRoute>
      )
    },
    // Pillar balance + pack catalog (top-up Newton processing units)
    {
      path: '/billing/pillars',
      element: (
        <ProtectedRoute>
          <Layout>
            <RequirePaidTier
              title="Areas need a paid plan"
              body="Areas are what your membership spends when a plan is written or re-run. Upgrade to top up — free accounts run on the included monthly allowance."
            >
              <Pillars />
            </RequirePaidTier>
          </Layout>
        </ProtectedRoute>
      )
    },
    // The customer's word for a pillar is an area (the nav entry says Areas);
    // /billing/pillars stays so old links keep working.
    {
      path: '/billing/areas',
      element: (
        <ProtectedRoute>
          <Layout>
            <RequirePaidTier
              title="Areas need a paid plan"
              body="Areas are what your membership spends when a plan is written or re-run. Upgrade to top up — free accounts run on the included monthly allowance."
            >
              <Pillars />
            </RequirePaidTier>
          </Layout>
        </ProtectedRoute>
      )
    },

    // Founder concierge pipeline — admin-only
    {
      path: '/admin/concierge',
      element: (
        <AdminRoute>
          <Layout>
            <ConciergeQueue />
          </Layout>
        </AdminRoute>
      )
    },
    // Reviewer self-service workspace — any authenticated user can apply.
    // The page handles the apply / pending / approved states internally.
    {
      path: '/reviewer',
      element: (
        <ProtectedRoute>
          <Layout>
            <ReviewerDashboard />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/reviewer/standing',
      element: (
        <ProtectedRoute>
          <Layout>
            <MyStandingPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/reviewer/plan',
      element: (
        <ProtectedRoute>
          <Layout>
            <ReviewerPlanPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      path: '/reviewer/earnings',
      element: (
        <ProtectedRoute>
          <Layout>
            <ReviewerEarningsPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // Expert review marketplace queue (v2). Any authenticated user
      // can land here; the backend filters to jobs that match the
      // viewer's track / level (returns empty list for non-experts).
      path: '/reviewer/queue',
      element: (
        <ProtectedRoute>
          <Layout>
            <ExpertReviewQueue />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // Where a reviewer does the work: edit sections, regenerate, record
      // progress, tick the checklist and sign off. The API behind this shipped
      // long before any screen called it.
      path: '/reviewer/work/:versionId',
      element: (
        <ProtectedRoute>
          <Layout>
            <ReviewerWorkConsole />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // A reviewer proposing a template change. Propose, never publish — it
      // goes through the same tiered approval the learning loop uses.
      path: '/reviewer/propose',
      element: (
        <ProtectedRoute>
          <Layout>
            <ProposeTemplateChangePage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // Where an applicant stands and what is actually left. The rail carried
      // all of this and nothing rendered it.
      path: '/reviewer/application',
      element: (
        <ProtectedRoute>
          <Layout>
            <ApplicationRailPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // The stage every application stopped at. RV2 asks for evidence, the
      // gate that vets it was fully built, the submit route existed — and
      // there was no screen, so nobody could take the step they were told to
      // take, and staff could not decide a stage nobody could submit (GC-113).
      path: '/reviewer/application/evidence',
      element: (
        <ProtectedRoute>
          <Layout>
            <EvidencePage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // Reviewer feedback into the learning loop. The write route existed and
      // was admin-only; widening the guard was half the fix — this is the half
      // that makes it reachable by the people it was widened for.
      path: '/reviewer/feedback',
      element: (
        <ProtectedRoute>
          <Layout>
            <ReviewerFeedbackPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // A reviewer's own working preferences: taking work, direct contact,
      // locations, pool sort. Stored server-side so they follow the reviewer
      // across devices.
      path: '/reviewer/availability',
      element: (
        <ProtectedRoute>
          <Layout>
            <ReviewerAvailabilityPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // The assessment accreditation is meant to rest on. Seven routes existed
      // with no screen, so approval rested on submitted evidence alone.
      path: '/reviewer/calibration',
      element: (
        <ProtectedRoute>
          <Layout>
            <CalibrationPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // The lead's side: choose sections, choose a specialist, set the split.
      path: '/reviewer/delegate/:jobId',
      element: (
        <ProtectedRoute>
          <Layout>
            <DelegateSectionsPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // Sections handed between reviewers — accept, start, submit to lead,
      // or hand back with a reason.
      path: '/reviewer/delegations',
      element: (
        <ProtectedRoute>
          <Layout>
            <DelegationsPage />
          </Layout>
        </ProtectedRoute>
      )
    },
    {
      // The customer's (or reviewer's) side of a dispute. Protected rather
      // than guest-accessible: raising one acts on a paid engagement.
      path: '/reviews/:jobId/dispute',
      element: (
        <ProtectedRoute>
          <Layout>
            <DisputePage />
          </Layout>
        </ProtectedRoute>
      )
    },

    // Dictionary Control: keep as foundational routes so we can provide
    // multi-page UX (list -> edit -> delete confirm) without colliding with the
    // dynamic-catalog route generator.
    {
      path: '/admin/dictionary/control',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryHome />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/data-types',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryDataTypes />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/data/:categoryEncoded',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryCategoryEntries />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/data/:categoryEncoded/new',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryEntryEdit />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/data/:categoryEncoded/:entryIdEncoded',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryEntryEdit />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/data/:categoryEncoded/:entryIdEncoded/view',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryEntryEdit />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/data/:categoryEncoded/:entryIdEncoded/delete',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryEntryDelete />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/templates',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryTemplates />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/templates/:templateIdEncoded',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryTemplateEdit />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/templates/:templateIdEncoded/view',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryTemplateEdit />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/templates/:templateIdEncoded/delete',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryTemplateDelete />
          </Layout>
        </AdminRoute>
      )
    },
    {
      path: '/admin/dictionary/control/settings',
      element: (
        <AdminRoute>
          <Layout>
            <DictionaryGlobalSettings />
          </Layout>
        </AdminRoute>
      )
    }
  ];

  /**
   * 🛡️ GUARD: MESH HYDRATION (Metric 13.4)
   * Logic: If the catalog is empty, the Dictionary Service is still synchronizing.
   * We return ONLY public routes and EXCLUDE the catch-all redirect.
   * This prevents the "Flash of Redirect" to the homepage while the Brain boots.
   */
  if (!catalog || catalog.length === 0) {
    return [...publicRoutes, ...foundationalPrivateRoutes];
  }

  // 2. Dynamic Private Routes (Orchestrated by Service #4)
  //
  // The catalog is a TREE: a nav group ("Payments", "Templates") carries
  // `children` and no path of its own. Route building walks it flat, so a
  // routable row keeps its route no matter how deep it sits in the nav —
  // otherwise moving an item under a group (e.g. /admin/identity into
  // "People & access") would silently 404 it. Only leaves carry paths.
  const flattenCatalog = (items) => (items || []).flatMap((item) => (
    Array.isArray(item.children) && item.children.length
      ? flattenCatalog(item.children)
      : [item]
  ));
  const reservedStaticPaths = new Set(foundationalPrivateRoutes.map((route) => route.path));

  // A statically-declared route may be a PATTERN, not a literal:
  // `/knowledge/type/:entityType` serves twenty-seven concrete paths that the
  // nav names individually. The set above only catches exact strings, so every
  // one of those rows looked unreserved, got a generated route, and — having
  // no `componentId` — rendered InternalServerError. Worse, it SHADOWED the
  // real page rather than merely duplicating it, because React Router ranks a
  // static segment above a dynamic one, so `/knowledge/type/product` beat
  // `/knowledge/type/:entityType` every time.
  //
  // The symptom is indistinguishable from a crash in the page itself, which is
  // what made it expensive to find: the page was fine and never rendered.
  const patternMatchers = foundationalPrivateRoutes
    .filter((route) => typeof route.path === 'string' && route.path.includes('/:'))
    .map((route) => new RegExp(
      `^${route.path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\/:[^/]+/g, '/[^/]+')}$`
    ));

  const coveredByPattern = (path) => patternMatchers.some((re) => re.test(path));
  const seenDynamicPaths = new Set();
  const dynamicRoutes = flattenCatalog(catalog)
    // Group rows have no path, and some nav rows are query-string variants of
    // one page ("Horizontal pillars" = /…/pillars?scope=horizontal). A route
    // path can't hold a query string, so match on the pathname alone.
    .filter((item) => typeof item.path === 'string' && item.path !== '/')
    .map((item) => ({ ...item, path: item.path.split('?')[0] }))
    .filter((item) => {
      if (reservedStaticPaths.has(item.path)) return false;
      if (coveredByPattern(item.path)) return false;
      if (seenDynamicPaths.has(item.path)) return false; // query-variants collapse to one route
      seenDynamicPaths.add(item.path);
      return true;
    })
    .map((item) => {
    const Component = componentMap[item.componentId];
    const isContextAdmin =
      item.path?.startsWith('/admin') ||
      (Array.isArray(item.roles) && item.roles.length === 1 && item.roles[0] === 'admin');
    
    // Select the correct guard based on the Protocol Roles defined in JSON
    const Guard = isContextAdmin ? AdminRoute : ProtectedRoute;

    return {
      path: item.path,
      element: (
        <Guard>
          <Layout>
            {Component ? (
              <Component />
            ) : (
              <InternalServerError />
            )}
          </Layout>
        </Guard>
      )
    };
    });

  /**
   * 3. Unified Route Registry
   * Logic: The catch-all (*) redirect is ONLY added here, at the very end,
   * once we are 100% sure the dynamic catalog has been processed.
   */
  return [
    ...publicRoutes,
    ...foundationalPrivateRoutes,
    ...dynamicRoutes,
    { path: '*', element: <GuestLayout><CmsPage /></GuestLayout> }
  ];
};
